import { RealTimeSttEventMarker } from './real-time-stt-event-marker'
import { RealTimeSttHearingMarker } from './real-time-stt-hearing-marker'
import { RealTimeSttPause } from './real-time-stt-pause'
import { RealTimeSttRemark } from './real-time-stt-remark'
import {
  RealTimeRemarkSearchHighlightingContext,
  RealTimeSttRemarkWithMetadata,
} from './real-time-stt-remark-with-metadata'
import { RealTimeSttSealingMarker } from './real-time-stt-sealing-marker'
import { RealTimeSttSessionMarker } from './real-time-stt-session-marker'
import {
  RealTimeSttTranscriptElementBase,
  RealTimeSttTranscriptElementType,
} from './real-time-stt-transcript-element-base'

export interface RemarkMetadata {
  readonly firstRemarkIdInGroup: string

  /**
   * Contains metadata about how to highlight parts of the content that match a search result.
   */
  readonly searchHighlightingContext?: RealTimeRemarkSearchHighlightingContext
}

export interface RealTimeSttMetadata extends RemarkMetadata {
  segmentId: string
  isInSession: boolean
  isSealed: boolean
  isHearing: boolean
  isWithinAutoGeneratedHearing: boolean
  isWithinHiddenSection: boolean
  /**
   * If this transcript element is inside a hearing or sealing,
   * We store the hearing/sealing start section/marker id,
   * To be able to open popovers via the sticky marker
   */
  hearingSectionId?: string
  sealingMarkerId?: string
  sessionMarkerId?: string
}

// Ensure all our transcript elements correctly implement the ElementBase
type EnforceExtends<T extends RealTimeSttTranscriptElementBase & { type: RealTimeSttTranscriptElementType }> = T
export type RealTimeSttTranscriptElement =
  | EnforceExtends<RealTimeSttPause>
  | EnforceExtends<RealTimeSttRemark>
  | EnforceExtends<RealTimeSttSessionMarker>
  | EnforceExtends<RealTimeSttSealingMarker>
  | EnforceExtends<RealTimeSttHearingMarker>
  | EnforceExtends<RealTimeSttEventMarker>

export type RealTimeSttTranscriptElementWithMetadata = RealTimeSttTranscriptElement & RealTimeSttMetadata

export function isSessionMarker(
  element: RealTimeSttTranscriptElement,
): element is RealTimeSttSessionMarker & RealTimeSttMetadata {
  return element.type === 'SessionMarker'
}

export function isSealingMarker(
  element: RealTimeSttTranscriptElement,
): element is RealTimeSttSealingMarker & RealTimeSttMetadata {
  return element.type === 'SealingMarker'
}

export function isHearingMarker(
  element: RealTimeSttTranscriptElement,
): element is RealTimeSttHearingMarker & RealTimeSttMetadata {
  return element.type === 'HearingMarker'
}

export function isPause(element: RealTimeSttTranscriptElement): element is RealTimeSttPause & RealTimeSttMetadata {
  return element.type === 'Pause'
}

export function isEventMarker(
  element: RealTimeSttTranscriptElement,
): element is RealTimeSttEventMarker & RealTimeSttMetadata {
  return element.type === 'EventMarker'
}

export function isRemark(element: RealTimeSttTranscriptElement): element is RealTimeSttRemarkWithMetadata {
  return element.type === 'Remark'
}

export const PREVIEW_PANE_REMARK_ID_PREFIX = 'preview-pane-'
